import type { FC, SVGProps } from "react";
import KkScrewGraphic, { type KkScrewSize, type MillimetersToPixels } from "./KkScrew";

type GapProps = SVGProps<SVGSVGElement> & {
  screw?: Partial<KkScrewSize> | null;
};

export const GAP_CONNECTOR_BASE_WIDTH_MM = 42;
export const GAP_CONNECTOR_BASE_THICKNESS_MM = 1.5;
export const GAP_CONNECTOR_ARM_WIDTH_MM = 7.8;
export const GAP_CONNECTOR_ARM_HEIGHT_MM = 10.3;
export const GAP_CONNECTOR_ARM_THICKNESS_MM = 1.5;
export const GAP_CONNECTOR_ARM_SPACING_MM = 2;
export const GAP_CONNECTOR_TOTAL_HEIGHT_MM = GAP_CONNECTOR_ARM_HEIGHT_MM + GAP_CONNECTOR_BASE_THICKNESS_MM;
export const GAP_CONNECTOR_STACK_TOP_Y_MM = GAP_CONNECTOR_ARM_HEIGHT_MM;
export const GAP_CONNECTOR_GROOVE_DEPTH_CLEARANCE_MM = 2;
export const GAP_CONNECTOR_TOP_SPAN_MM = GAP_CONNECTOR_ARM_WIDTH_MM * 2 + GAP_CONNECTOR_ARM_SPACING_MM;

const FILL_COLOR = "#b8b8b8";
const STROKE_COLOR = "#111";
const STROKE_WIDTH = 0.16;
const millimetersToGraphicUnits: MillimetersToPixels = (millimeters) => millimeters;

const Gap: FC<GapProps> = ({ screw, ...props }) => {
  const armBottomY = GAP_CONNECTOR_ARM_HEIGHT_MM;
  const baseY = armBottomY;
  const baseBottomY = baseY + GAP_CONNECTOR_BASE_THICKNESS_MM;
  const centerX = GAP_CONNECTOR_BASE_WIDTH_MM / 2;
  const leftArmInnerX = centerX - GAP_CONNECTOR_ARM_SPACING_MM / 2;
  const rightArmInnerX = centerX + GAP_CONNECTOR_ARM_SPACING_MM / 2;
  const leftArmOuterX = leftArmInnerX - GAP_CONNECTOR_ARM_WIDTH_MM;
  const rightArmOuterX = rightArmInnerX + GAP_CONNECTOR_ARM_WIDTH_MM;
  const leftWebInnerX = leftArmInnerX - GAP_CONNECTOR_ARM_THICKNESS_MM;
  const rightWebInnerX = rightArmInnerX + GAP_CONNECTOR_ARM_THICKNESS_MM;
  const bendY = GAP_CONNECTOR_ARM_THICKNESS_MM * 2.1;
  const baseCapInset = 3;
  const screwCenterX = GAP_CONNECTOR_BASE_WIDTH_MM * 0.75;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${GAP_CONNECTOR_BASE_WIDTH_MM} ${GAP_CONNECTOR_TOTAL_HEIGHT_MM}`}
      aria-hidden="true"
      overflow="visible"
      shapeRendering="geometricPrecision"
      {...props}
    >
      {screw && (
        <KkScrewGraphic
          screw={screw}
          toPixels={millimetersToGraphicUnits}
          centerX={screwCenterX}
          topY={baseBottomY}
          showHead={false}
        />
      )}
      <rect
        x="0"
        y={baseY}
        width={GAP_CONNECTOR_BASE_WIDTH_MM}
        height={GAP_CONNECTOR_BASE_THICKNESS_MM}
        fill={FILL_COLOR}
        stroke={STROKE_COLOR}
        strokeWidth={STROKE_WIDTH}
      />
      <line
        x1={baseCapInset}
        y1={baseY}
        x2={baseCapInset}
        y2={GAP_CONNECTOR_TOTAL_HEIGHT_MM}
        stroke={STROKE_COLOR}
        strokeWidth={STROKE_WIDTH * 0.75}
      />
      <line
        x1={GAP_CONNECTOR_BASE_WIDTH_MM - baseCapInset}
        y1={baseY}
        x2={GAP_CONNECTOR_BASE_WIDTH_MM - baseCapInset}
        y2={GAP_CONNECTOR_TOTAL_HEIGHT_MM}
        stroke={STROKE_COLOR}
        strokeWidth={STROKE_WIDTH * 0.75}
      />
      <path
        d={`M ${leftArmOuterX} 0
          H ${leftWebInnerX}
          C ${leftWebInnerX + 1.2} 0 ${leftArmInnerX} ${bendY - 1.2} ${leftArmInnerX} ${bendY}
          V ${armBottomY}
          H ${leftWebInnerX}
          V ${bendY}
          C ${leftWebInnerX} ${GAP_CONNECTOR_ARM_THICKNESS_MM + 0.35}
            ${leftWebInnerX - 0.35} ${GAP_CONNECTOR_ARM_THICKNESS_MM}
            ${leftWebInnerX - 1} ${GAP_CONNECTOR_ARM_THICKNESS_MM}
          H ${leftArmOuterX}
          Z`}
        fill={FILL_COLOR}
        stroke={STROKE_COLOR}
        strokeLinejoin="round"
        strokeWidth={STROKE_WIDTH}
      />
      <path
        d={`M ${rightArmOuterX} 0
          H ${rightWebInnerX}
          C ${rightWebInnerX - 1.2} 0 ${rightArmInnerX} ${bendY - 1.2} ${rightArmInnerX} ${bendY}
          V ${armBottomY}
          H ${rightWebInnerX}
          V ${bendY}
          C ${rightWebInnerX} ${GAP_CONNECTOR_ARM_THICKNESS_MM + 0.35}
            ${rightWebInnerX + 0.35} ${GAP_CONNECTOR_ARM_THICKNESS_MM}
            ${rightWebInnerX + 1} ${GAP_CONNECTOR_ARM_THICKNESS_MM}
          H ${rightArmOuterX}
          Z`}
        fill={FILL_COLOR}
        stroke={STROKE_COLOR}
        strokeLinejoin="round"
        strokeWidth={STROKE_WIDTH}
      />
      <path
        d={`M ${leftArmInnerX} ${armBottomY - 0.5} Q ${leftArmInnerX} ${armBottomY - 0.1} ${leftArmInnerX} ${armBottomY}
          M ${rightArmInnerX} ${armBottomY - 0.5} Q ${rightArmInnerX} ${armBottomY - 0.1} ${rightArmInnerX} ${armBottomY}`}
        fill="none"
        stroke={STROKE_COLOR}
        strokeWidth={STROKE_WIDTH * 0.75}
      />
    </svg>
  );
};

export default Gap;
